// Apolo IDE — SessionComposer (in-session variant of CodeComposer).
//
// Same UX as CodeComposer (textarea + permissions toggle + model picker)
// but always submits into an existing session. Epic 6 wires this to the
// real streaming endpoint; Epic 5 just persists the user turn via POST.
//
// Sprint v4 Epic S1 — adds an optional Compare Mode toggle. When the parent
// passes `compareMode` props, a toggle appears in the toolbar and the
// submit action is delegated to `compareMode.onSubmit` instead of the
// regular `onSubmit`. The keyboard shortcut (⇧⌘P) is owned by the
// useCompareMode hook upstream.
//
// pos-s5 restructure — the toolbar is now the clean Cursor layout: PRIMARY
// pills inline (Plan · Model · Add context), a round 28px send ICON, and ALL
// secondary controls (Auto-aprovar · Spec · Skills · Equipe · Esforço · Falar ·
// Hands-free · Atalhos) tucked behind the "…" overflow — one click away
// (compaction ≠ removal). The ONE exception is the safety state: when
// auto-approve is ON (the risky mode), an amber pill stays visible inline so a
// destructive auto-run is never hidden (Glass Box).

import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowUp, ClipboardList, Command, Eye, FileText, Gauge, GitCommit, Loader2, MoreHorizontal, MousePointer2, Paperclip, Plus, ShieldCheck, ShieldQuestion, Sparkles, Square, X } from 'lucide-react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { useAgentStore } from '../../store/useAgentStore.js';
import { useComposerModels } from '../../hooks/useComposerModels.js';
import { useComposerInput } from '../../hooks/useComposerInput.js';
import { parseSlashCommand } from '../../lib/slashCommands.js';
import { useSessionObserver } from '../../hooks/useSessionObserver.js';
import CompareModeToggle from './CompareMode/CompareModeToggle.jsx';
import TeamModeToggle from './team/TeamModeToggle.jsx';
import TeamSettingsPopover from './team/TeamSettingsPopover.jsx';
import SlashCommandAutocomplete from './SlashCommandAutocomplete.jsx';
import { removeSlashToken } from '../../hooks/useSlashCommands.js';
import FileMentionAutocomplete from './FileMentionAutocomplete.jsx';
import ChatMentionAutocomplete, { applyChatMention } from './ChatMentionAutocomplete.jsx';
import ComposerMenuPortal from './ComposerMenuPortal.jsx';
import ComposerAtMenu from './ComposerAtMenu.jsx';
import { applyFileMention } from '../../lib/fileMention.js';
import InvokedSkillsBar from './InvokedSkillsBar.jsx';
import ModelDropdown from './ModelDropdown.jsx';
import ComposerOverflowMenu from './ComposerOverflowMenu.jsx';
import MicButton from '../MicButton.jsx';
import { modelSupportsReasoning } from '../../lib/reasoningEffort.js';
import HandsFreeButton from '../HandsFreeButton.jsx';
import { sessionRoot } from '../../lib/sessionRoot.js';
import AttachmentChips from './AttachmentChips.jsx';
import AttachmentDropOverlay from './AttachmentDropOverlay.jsx';
import SkillTemplatesModal from './SkillTemplatesModal.jsx';
import MentionChip from './MentionChip.jsx';
import ContextUsagePopover from './ContextUsagePopover.jsx';
import useContextUsage from '../../hooks/useContextUsage.js';
import ComposerFooterStrip from './chat/ComposerFooterStrip.jsx';
import GitRefMenu from './chat/GitRefMenu.jsx';
import {
  useFileAttachments,
  serializeAttachmentsToPrompt,
} from '../../hooks/useFileAttachments.js';
import { useMentionsResolver } from '../../hooks/useMentionsResolver.js';
import { parseMentions, resolveAndEnrich } from '../../lib/mentions.js';
import { classifyPaste } from '../../lib/pasteIntent.js';
import './chat/redesign.css';

export default function SessionComposer({ onSubmit, sessionId, compareMode, running = false, onStop, onQueue, queuedCount = 0, onOpenContext, contextFiles = null }) {
  // G3 — the composer DRAFT model (text + invoked-skill pills + design-element pills)
  // lives in useComposerInput; the pure submission logic (hasContent / assembly / dedup /
  // mention splice) lives in lib/composer/composeSubmission.
  const {
    text, setText,
    invokedSkills, setInvokedSkills, addSkill,
    designEls, setDesignEls, addDesign,
    removeMention, hasContent, buildSubmission,
  } = useComposerInput();
  const [busy, setBusy] = useState(false);
  const [showSkillsModal, setShowSkillsModal] = useState(false);
  // (Pedro 2026-06-25, sprint cursor-strip-3 T12) — drop-up de commits reais
  // pra inserir referência no draft.
  const [showRefMenu, setShowRefMenu] = useState(false);
  const [showContextUsage, setShowContextUsage] = useState(false);
  const textareaRef = useRef(null);
  // Respeita prefers-reduced-motion no swap Enviar↔Parar (a11y).
  const reduce = useReducedMotion();
  const chipClass =
    'inline-flex h-6 shrink-0 items-center gap-1 rounded-md border border-apolo-borderSubtle bg-apolo-surface2/40 px-2 text-xs text-apolo-textSecondary transition hover:border-apolo-borderStrong hover:bg-apolo-surface2 hover:text-apolo-textPrimary focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing';

  // Sprint v11 E2 — parse mentions live so the UI can preview chips.
  const pendingMentions = useMemo(() => parseMentions(text), [text]);

  // (T2) removeMention (splice the raw "@type:target" token, index-drift guarded) lives
  // in the composer engine now — bound in useComposerInput.
  // Pull the session row for its directory so mention resolution can read
  // files relative to the worktree root via Electron IPC.
  const activeSession = useAgentStore((s) =>
    s.code.sessions.find((sess) => sess.id === sessionId),
  );
  const mentionIO = useMentionsResolver({
    root: activeSession?.directory || '',
    sessionId,
  });
  // (Pedro 2026-06-25, sprint cursor-strip-4 round 9) — o branch saiu do
  // footer-strip (branch={null}) porque a GitConnectionBar abaixo já mostra
  // o branch; logo o useGitContext daqui virou dead-code e foi removido.
  const {
    attachments,
    dragOver,
    uploadError,
    fileInputRef,
    removeAttachment,
    clear: clearAttachments,
    openFilePicker,
    dropZoneHandlers,
    fileInputHandlers,
    addFiles,
  } = useFileAttachments();

  // Arquivos do painel Contexto (store persistente, liftado no SessionWorkspace)
  // expostos ao menu do `@` — picar um anexa à mensagem atual. Sem o painel
  // (compareMode, telas sem contexto) a lista é vazia e o `@` age como antes.
  const contextItems = useMemo(
    () => (contextFiles?.attachments || []).map((a) => ({ id: a.id, name: a.name, kind: a.kind })),
    [contextFiles?.attachments],
  );

  // Cmd+V smart paste. Uma imagem da área de transferência (screenshot, imagem
  // copiada) vira ANEXO pendente pela MESMA via do picker/drop — nada de base64
  // poluindo o textarea. Texto grande ou normal cai no paste nativo (sem
  // preventDefault), então a digitação comum nunca é interceptada.
  function handlePaste(e) {
    let intent;
    try {
      intent = classifyPaste(e.clipboardData);
    } catch {
      // Nunca engole o paste por erro de classificação — deixa o nativo seguir.
      return;
    }
    if (intent.kind === 'image' && intent.files?.length) {
      e.preventDefault();
      addFiles(intent.files);
      return;
    }
    // 'large-text' e 'text' → paste nativo (não chamamos preventDefault).
  }

  function handlePickCommand(cmd, range) {
    // A skill vira PILL: remove o /token ativo do textarea (só a prosa fica) e
    // acumula o comando em invokedSkills — reinjetado no prompt no envio.
    // `range` (caret-aware) remove EXATAMENTE o token digitado e PRESERVA o texto
    // DEPOIS do cursor — é o que faz invocar a skill no MEIO da frase funcionar.
    // Sem range → fallback end-anchored (retrocompatível).
    const { text: nextText, caret: nextCaret } = removeSlashToken(text, range);
    setText(nextText);
    addSkill(cmd); // deduped by slash_command in the engine
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      if (typeof nextCaret === 'number') el.setSelectionRange(nextCaret, nextCaret);
    });
  }

  // Autocomplete de @file:/@folder: — troca o token pelo path escolhido. Diretório
  // mantém a '/' e re-abre o menu (navegar fundo); arquivo fecha com espaço. A
  // sintaxe @file:<path> já é resolvida + injeta o conteúdo no prompt no envio.
  function handlePickFileMention(detected, entry) {
    if (!detected || !entry) return;
    setText(applyFileMention(text, detected, entry));
    requestAnimationFrame(() => textareaRef.current?.focus());
  }

  // Aba-Apolo C2 — `#chat:` pick: insere o token da conversa passada escolhida.
  function handlePickChatMention(detected, session) {
    if (!detected || !session) return;
    setText(applyChatMention(text, detected, session));
    requestAnimationFrame(() => textareaRef.current?.focus());
  }

  // Cursor "@" menu pick — strip the @token, then run the mapped action. Each id
  // maps to a REAL feature (toggle a mode / open the picker-modal / hand off to
  // the @file: typeahead). `ui`/`setPref`/openFilePicker/setShowSkillsModal are
  // resolved at call time (post-render), so hoisting is fine.
  function handleAtMenuSelect(id, detected) {
    if (!id || !detected) return;
    const before = text.slice(0, detected.start);
    const after = text.slice(detected.end);
    let nextText = before + after;
    // Arquivo do painel Contexto → anexa à MENSAGEM atual (é enviado no turno).
    // O painel Contexto persiste (é outro store); aqui só copiamos o File pro
    // store de anexos por-mensagem do composer, que limpa no envio.
    if (id.startsWith('ctx:')) {
      const attId = id.slice(4);
      const item = contextFiles?.attachments?.find((a) => String(a.id) === attId);
      if (item?.file) addFiles([item.file]);
      setText(nextText);
      requestAnimationFrame(() => textareaRef.current?.focus());
      return;
    }
    switch (id) {
      case 'plan':
        if (ui.team_mode) setPref('team_mode', false);
        setPref('plan_mode', true);
        break;
      case 'multitask':
        if (planMode) setPref('plan_mode', false);
        setPref('team_mode', true);
        break;
      case 'ask':
        setPref('permissions_mode', 'ask');
        break;
      case 'image':
        openFilePicker();
        break;
      case 'skills':
        setShowSkillsModal(true);
        break;
      case 'files':
        nextText = `${before}@file:${after}`; // hand off to the @file: typeahead
        break;
      default:
        break;
    }
    setText(nextText);
    requestAnimationFrame(() => textareaRef.current?.focus());
  }
  const ui = useAgentStore((s) => s.code.ui);
  const chatMode = useAgentStore((s) => s.code.chatModes?.[sessionId] || activeSession?.flags?.collaboration_mode);
  const setChatMode = useAgentStore((s) => s.setChatMode);
  const planMode = Boolean(ui.plan_mode || chatMode === 'plan');
  // (Pedro) live context-window usage for the composer drop-up (moved out of the
  // outputs Menu). Conversation footprint vs the model's window.
  const contextUsage = useContextUsage(sessionId, ui.model);
  const setPref = useAgentStore((s) => s.setUiPref);
  const updateCodeSession = useAgentStore((s) => s.updateCodeSession);
  const saveSessionToBrain = useAgentStore((s) => s.saveSessionToBrain);
  // Sprint v9 polish — agrega adapters configurados (Sprint v5) ao dropdown
  // sem tocar no MODEL_REGISTRY intocável.
  const { groups: modelGroups } = useComposerModels();
  const observe = useSessionObserver(sessionId);

  // Ao trocar de sessão, alinha o dropdown ao model persistido nela.
  useEffect(() => {
    // Foca o composer ao abrir/criar/trocar de sessão. O SessionWorkspace NÃO
    // remonta por sessão (reusa a instância — ver agentRunStore), então o autofocus
    // de mount não re-dispara: sem isto, criar/trocar de sessão deixava o composer
    // SEM foco, e só dava pra digitar clicando (inacessível por teclado/automação).
    // focus() num composer oculto (aba inativa, display:none) é no-op — o visível
    // é o único que realmente pega o foco, então não precisa de guard de workspace.
    requestAnimationFrame(() => textareaRef.current?.focus());
    if (!activeSession?.model) return;
    setPref('model', activeSession.model);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- só na troca de sessão
  }, [sessionId]);

  // O botão de Stop anuncia "(Esc)", mas nada estava ligado — Escape nunca parava
  // o run (só dava clicando, e num clique bloqueado o run ficava imparável). Liga o
  // atalho de verdade: Escape para o agente quando está RODANDO. Guard de foco: se
  // estiver digitando num campo (ou com um menu do composer aberto), Escape mantém o
  // comportamento normal (fechar menu/limpar) e não derruba o run.
  useEffect(() => {
    if (!running || typeof onStop !== 'function') return undefined;
    function onKey(e) {
      if (e.key !== 'Escape') return;
      const el = typeof document !== 'undefined' ? document.activeElement : null;
      if (el && (el.tagName === 'TEXTAREA' || el.tagName === 'INPUT')) return;
      e.preventDefault();
      onStop();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [running, onStop]);

  async function handleModelChange(id) {
    setPref('model', id);
    if (sessionId && id) {
      try {
        await updateCodeSession(sessionId, { model: id });
      } catch {
        /* best effort — ui.model já mudou pro próximo envio */
      }
    }
  }

  // Sprint v8 C2/C3 — listen for apolo:insertComposer (workflow shortcuts +
  // SkillCommandPalette pick). Appends text to current draft and focuses.
  useEffect(() => {
    function onInsert(e) {
      // BUG-3 — AppShell keep-alive leaves the Code AND Chat composers BOTH mounted
      // (hidden via display:none); each listens on window for apolo:insertComposer.
      // Without this guard a preview pick (or any insert) appended to every mounted
      // composer, scattering text into hidden tabs. Only the active workspace's
      // composer consumes the event.
      if (useAgentStore.getState().activeWorkspace !== 'code') return;
      const insertText = e.detail?.text;
      if (typeof insertText !== 'string') return;
      setText((prev) => prev + insertText);
      requestAnimationFrame(() => textareaRef.current?.focus());
    }
    function onFocusComposer() {
      requestAnimationFrame(() => textareaRef.current?.focus());
    }
    function onDesignElement(e) {
      if (useAgentStore.getState().activeWorkspace !== 'code') return;
      const { label, prompt } = e.detail || {};
      if (!label || !prompt) return;
      // acumula (várias pills); dedupe pelo prompt no engine — clicar o MESMO elemento
      // de novo não duplica a pill.
      addDesign({ label, prompt });
      requestAnimationFrame(() => textareaRef.current?.focus());
    }
    window.addEventListener('apolo:insertComposer', onInsert);
    window.addEventListener('apolo:focusComposer', onFocusComposer);
    window.addEventListener('apolo:designElement', onDesignElement);
    return () => {
      window.removeEventListener('apolo:insertComposer', onInsert);
      window.removeEventListener('apolo:focusComposer', onFocusComposer);
      window.removeEventListener('apolo:designElement', onDesignElement);
    };
    // mount-once: the listeners are stable and the draft ops (setText/addDesign) are
    // stable refs from useComposerInput (useState setter + useCallback).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    // G3 — the submit guard + the outgoing-text assembly (skill pills reinjected in FRONT,
    // then design-element prompts, then the trimmed draft) live in the composer engine.
    // The /save special is still detected even after a skill became a pill.
    if (!hasContent(attachments)) return;
    const { value, skillPrefix, baseValue } = buildSubmission();
    const cmd = parseSlashCommand(skillPrefix ? `${skillPrefix} ${value}` : value);
    if (cmd?.name === 'save' && sessionId) {
      setBusy(true);
      try {
        await saveSessionToBrain(sessionId);
        setText('');
        setInvokedSkills([]);
      } finally {
        setBusy(false);
      }
      return;
    }
    // Sprint v8.3 — anexa lista de arquivos ao prompt textual (chat.js guarded — só
    // metadata por enquanto). baseValue (engine) já colou skill prefix + design prompts +
    // draft; o parser da bolha re-colapsa cada bloco de design numa pill.
    const withAttachments = serializeAttachmentsToPrompt(baseValue, attachments);

    // Sprint v11 E2 — resolve @file/@folder/@docs/@symbol mentions to
    // <context> blocks before sending. Falls back to raw text on failure.
    let effectiveText = withAttachments;
    let resolvedMentions = [];
    if (parseMentions(withAttachments).length > 0) {
      try {
        const enriched = await resolveAndEnrich(withAttachments, mentionIO);
        effectiveText = enriched.enrichedText;
        resolvedMentions = enriched.mentions;
      } catch {
        // best effort — keep the raw text so the user still gets a response
      }
    }
    setBusy(true);
    observe('composer_submit', {
      length: effectiveText.length,
      model: ui.model,
      permissions_mode: ui.permissions_mode,
      attachments: attachments.length,
      mentions: resolvedMentions.length,
    });
    // Limpa o rascunho assim que despachamos. O parent já renderiza o turno do
    // usuário de forma otimista e qualquer erro de stream aparece na própria
    // transcrição — uma resposta que falha NÃO pode deixar o texto preso na
    // caixa (senão o usuário tem que apagar na mão). `effectiveText`/`attachments`
    // já foram capturados acima, então limpar antes do await é seguro. Espelha o
    // CodeComposer, que nunca acoplou a limpeza ao sucesso do envio.
    setText('');
    clearAttachments();
    // Anotações do preview: avisa o useDevPreview que o prompt único foi
    // despachado → ele limpa os badges numerados na página e zera o contador.
    if (designEls.length > 0) window.dispatchEvent(new CustomEvent('apolo:designSubmitted'));
    setDesignEls([]);
    setInvokedSkills([]);
    try {
      // When Compare Mode is active, fan out to the compareMode handler
      // (which calls /api/agents/compare with the configured model lineup)
      // instead of the single-model streaming path. The single-model model
      // selector is hidden in the UI when active so this branch matches
      // user intent.
      if (compareMode?.active && typeof compareMode.onSubmit === 'function') {
        await compareMode.onSubmit({ text: effectiveText, sessionId });
      } else if (running) {
        // BUG (Pedro): enviar enquanto roda não podia ENFILEIRAR. Enquanto há um
        // turno em andamento NUNCA iniciamos um turno concorrente (onSubmit) — o
        // envio entra na FILA e o SessionWorkspace despacha quando o turno terminar
        // (ou após Stop). Sem onQueue disponível, é no-op (nunca submete concorrente).
        if (typeof onQueue === 'function') {
          onQueue({
            text: effectiveText,
            model: ui.model,
            permissions_mode: ui.permissions_mode,
            plan_mode: !!planMode,
            attachments,
          });
        }
      } else {
        await onSubmit?.({
          text: effectiveText,
          model: ui.model,
          permissions_mode: ui.permissions_mode,
          plan_mode: !!planMode,
          attachments,
        });
      }
    } catch (err) {
      // O erro já foi exibido na transcrição (useAgentStream renderiza a bolha
      // de erro). Aqui só registramos telemetria. NÃO re-lançar: o handler de
      // Enter chama handleSubmit sem await, então um throw viraria unhandled
      // rejection sem ninguém pra tratar.
      observe('composer_abort', { reason: 'error', error: err?.message });
    } finally {
      setBusy(false);
    }
  }

  // Há algo pra mandar? (texto/anexo/skill/elemento). Independente de `busy`.
  const hasContentNow = hasContent(attachments);
  const canSend = hasContentNow && !busy;
  // WSX (forense Pro Esporte) — ENFILEIRAR enquanto RODA não pode depender de `!busy`.
  // Bug: `busy` fica true o RUN inteiro (o composer dá `await onSubmit` → handleSend →
  // runAgent → run inteiro), então o botão de fila sumia e o Enter era engolido durante
  // toda a execução. A fila (else-if running → onQueue) é síncrona e rápida; só exige
  // conteúdo. `busy` segue barrando só o SUBMIT inicial (anti duplo-envio fora de run).
  const canQueue = running && hasContentNow;
  const autoApproveOn = ui.permissions_mode !== 'ask';

  return (
    <form
      onSubmit={handleSubmit}
      {...dropZoneHandlers}
      className={
        // E3-T01 — sem mx- próprio: a largura/centragem vem do wrapper de leitura
        // centrado no SessionWorkspace (compartilha a coluna do feed).
        // (Pedro 2026-06-25, sprint cursor-strip-4 round 6) — mb-1 (era mb-3):
        // aproxima o composer da barra git de baixo.
        'rx-composer relative mb-1 ' +
        (dragOver ? 'ring-2 ring-apolo-accent/40' : '')
      }
    >
      <AttachmentDropOverlay visible={dragOver} />
      {pendingMentions.length > 0 && (
        <div className="flex flex-wrap gap-1.5" aria-label="Menções detectadas">
          {pendingMentions.map((m, idx) => (
            <MentionChip
              key={`${m.start}-${idx}`}
              type={m.type}
              target={m.target}
              onRemove={() => removeMention(m)}
            />
          ))}
        </div>
      )}
      {designEls.length > 0 && (
        <div className="mb-1.5 flex flex-wrap items-center gap-1.5" aria-label="Elementos selecionados no preview">
          {designEls.map((d, i) => (
            <span
              key={`${d.prompt}-${i}`}
              className="inline-flex h-6 items-center gap-1.5 rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-2 text-xs text-apolo-textSecondary"
            >
              <MousePointer2 size={12} className="text-apolo-accent" aria-hidden="true" />
              <code className="font-medium text-apolo-textPrimary">{d.label}</code>
              <button
                type="button"
                aria-label={`Remover ${d.label}`}
                onClick={() => setDesignEls((prev) => prev.filter((_, j) => j !== i))}
                className="ml-0.5 inline-flex h-4 w-4 items-center justify-center rounded text-apolo-textTertiary transition hover:bg-apolo-surface3 hover:text-apolo-textPrimary focus:outline-none focus-visible:ring-1 focus-visible:ring-apolo-accentRing"
              >
                <X size={11} aria-hidden="true" />
              </button>
            </span>
          ))}
          {/* Olho: reabre o browser do preview na aba Code pra ver os badges
              numerados das anotações (Pedro: "pequeno botão com o olho"). */}
          <button
            type="button"
            aria-label="Abrir preview no navegador"
            title="Abrir preview no navegador"
            onClick={() => useAgentStore.getState().setPreviewPanelOpen(true)}
            className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-apolo-borderSubtle bg-apolo-surface2 text-apolo-textTertiary transition hover:bg-apolo-surface3 hover:text-apolo-textPrimary focus:outline-none focus-visible:ring-1 focus-visible:ring-apolo-accentRing"
          >
            <Eye size={12} aria-hidden="true" />
          </button>
        </div>
      )}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        {...fileInputHandlers}
        className="hidden"
        aria-hidden="true"
      />
      {/* pos-s5 clean toolbar (Cursor-style): input no TOPO, controles no RODAPÉ
          (order-last). PRIMÁRIOS inline: Plan · Modelo · Add context. SECUNDÁRIOS
          (Auto-aprovar · Spec · Skills · Equipe · Esforço · Falar · Hands-free ·
          Atalhos) no overflow "…" a 1 clique (compactação ≠ remoção). Send =
          ícone redondo 28px. Exceção de SEGURANÇA: auto-aprovar LIGADO mostra um
          pill âmbar inline (Glass Box — auto-run destrutivo nunca escondido). */}
      <div className="rx-composer-toolbar order-last">
        <button type="button" onClick={openFilePicker} className="rx-cpill workbench-attach" aria-label="Adicionar anexo" title="Adicionar anexo">
          <Paperclip size={15} aria-hidden="true" />
        </button>
        <button type="button" aria-pressed={chatMode === 'auto'}
          aria-label="Modo Auto: ajustar esforço e orçamento à tarefa"
          title="Auto ajusta esforço e limites à tarefa e preserva suas permissões"
          onClick={() => { setChatMode?.(sessionId, chatMode === 'auto' ? 'chat' : 'auto'); setPref('plan_mode', false); }}
          className={`rx-cpill workbench-plan-toggle ${chatMode === 'auto' ? 'workbench-plan-toggle-active' : ''}`}>
          <Sparkles size={12} aria-hidden="true" /><span>Auto</span>
        </button>
            <button
              type="button"
              onClick={() => {
                const next = !planMode;
                // Plan e Equipe são mutuamente exclusivos (planejar vs fan-out).
                if (next && ui.team_mode) setPref('team_mode', false);
                if (next && compareMode?.active) compareMode.onToggle?.(false);
                if (!next && chatMode === 'plan') setChatMode?.(sessionId, null);
                setPref('plan_mode', next);
              }}
              aria-pressed={!!planMode}
              aria-label="Alternar Plan Mode (propor plano antes de executar)"
              title="Plan Mode — o agente propõe um plano com TODOs e aguarda sua aprovação antes de executar"
              className={`rx-cpill workbench-plan-toggle ${planMode ? 'workbench-plan-toggle-active' : ''}`}
            >
              <ClipboardList size={12} aria-hidden="true" />
              <span>{planMode ? 'Planejando' : 'Planejar'}</span>
            </button>
        {/* PRIMÁRIO inline: só o Modelo. Plan · Add context · Ref foram pro menu
            "+" (Pedro 2026-07-11 — "todos esses botões dentro do +"). O toggle de
            segurança (Perguntar/Auto-aprovar) fica inline por doutrina Glass Box. */}
        <ModelDropdown
          value={ui.model}
          onChange={handleModelChange}
          groups={modelGroups}
          triggerClassName="rx-cpill"
        />
        {/* SEGURANÇA — permissões SEMPRE visível inline (Glass Box). Antes só
            aparecia quando LIGADO, então clicar pra desligar fazia o controle
            SUMIR do composer (Pedro). Agora é um toggle persistente: OFF =
            "Perguntar" (neutro), ON = "Auto-aprovar" (âmbar). Clicar alterna. */}
        <button
          type="button"
          onClick={() => setPref('permissions_mode', autoApproveOn ? 'ask' : 'ignore')}
          aria-label={
            autoApproveOn
              ? 'Auto-aprovar ligado — clique para voltar a Perguntar'
              : 'Modo Perguntar — clique para ligar Auto-aprovar'
          }
          title={
            autoApproveOn
              ? 'Auto-aprovar LIGADO: ações destrutivas passam sem confirmação. Clique para voltar a Perguntar.'
              : 'Modo Perguntar: ações destrutivas pedem confirmação. Clique para ligar Auto-aprovar.'
          }
          className={`rx-cpill${autoApproveOn ? ' rx-warn' : ''}`}
        >
          {autoApproveOn ? (
            <ShieldCheck size={11} aria-hidden="true" />
          ) : (
            <ShieldQuestion size={11} aria-hidden="true" />
          )}
          {autoApproveOn ? 'Auto-aprovar' : 'Perguntar'}
        </button>

        <div className="workbench-composer-actions ml-auto flex shrink-0 items-center gap-1.5">
          {/* (Pedro) Context Usage — white circular trigger next to "mais ações"
              (···); opens a drop-UP with the conversation's context footprint.
              Moved here from the outputs Menu. */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowContextUsage((v) => !v)}
              aria-expanded={showContextUsage}
              aria-label={
                contextUsage?.pct != null
                  ? `Uso de contexto: ${contextUsage.pct}% da janela`
                  : 'Uso de contexto da janela'
              }
              title="Uso de contexto"
              className={
                'rx-ctxu-btn' +
                (contextUsage?.pct != null ? ' rx-ctxu-btn--pct' : '') +
                (contextUsage?.pct >= 85 ? ' is-high' : '')
              }
            >
              <Gauge size={14} aria-hidden="true" />
              {contextUsage?.pct != null && <span>{contextUsage.pct}%</span>}
            </button>
            {showContextUsage && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  aria-hidden="true"
                  onClick={() => setShowContextUsage(false)}
                />
                <div className="absolute bottom-full right-0 z-20 mb-2">
                  <ContextUsagePopover usage={contextUsage} />
                </div>
              </>
            )}
          </div>
          {/* (Pedro 2026-06-25, sprint cursor-strip-4 round 3) — o "mais
             opções" agora é um "+" dentro de um círculo igual ao botão de
             uso de contexto (rx-ctxu-btn) ao lado. */}
      <HandsFreeButton inline
        toolContext={{ root: sessionRoot(activeSession), sessionId }}
        onSubmit={async (transcribed) => {
          setBusy(true);
          try {
            if (compareMode?.active && typeof compareMode.onSubmit === 'function') {
              await compareMode.onSubmit({ text: transcribed, sessionId });
            } else {
              await onSubmit?.({
                text: transcribed,
                model: ui.model,
                permissions_mode: ui.permissions_mode,
                plan_mode: !!planMode,
                attachments: [],
              });
            }
          } finally {
            setBusy(false);
          }
        }}
      />
          <ComposerOverflowMenu icon={MoreHorizontal} triggerClassName="rx-ctxu-btn">
            {/* PRIMÁRIOS movidos pro "+" (Pedro 2026-07-11 — "todos esses botões
                dentro do +"): Plan · Add context · Ref. Mesmos handlers, só mudou
                ONDE renderizam. */}

            <button
              type="button"
              role="menuitem"
              onClick={onOpenContext || openFilePicker}
              title={onOpenContext
                ? 'Abrir a aba Contexto — anexar arquivos (qualquer tipo) e salvar conhecimento'
                : 'Adicionar contexto — anexar arquivos (qualquer tipo)'}
              aria-label={onOpenContext ? 'Abrir aba de contexto' : 'Anexar arquivos'}
              data-testid="composer-add-context"
              className={`${chipClass} w-full justify-start`}
            >
              <Plus size={12} aria-hidden="true" />
              Add context
            </button>
            <div className="relative w-full">
              <button
                type="button"
                role="menuitem"
                onClick={() => setShowRefMenu((v) => !v)}
                aria-expanded={showRefMenu}
                aria-label="Inserir referência de commit"
                title="Inserir referência de commit (git log)"
                data-testid="composer-ref-button"
                className={`${chipClass} w-full justify-start`}
              >
                <GitCommit size={12} aria-hidden="true" />
                Ref
              </button>
              <GitRefMenu
                open={showRefMenu}
                onClose={() => setShowRefMenu(false)}
                root={activeSession?.directory || ''}
                anchorClassName="absolute bottom-full right-0 z-50 mb-2"
                onInsert={(ref) => {
                  setText((cur) => (cur ? `${cur.replace(/\s*$/, '')} ${ref} ` : `${ref} `));
                  requestAnimationFrame(() => textareaRef.current?.focus());
                }}
              />
            </div>
            <div className="my-1 h-px bg-apolo-borderSubtle" aria-hidden="true" />
            {/* permissões (Perguntar / Auto-aprovar) agora é um toggle SEMPRE
                visível inline no toolbar — não duplicar no overflow. */}
            {/* MOVIDO — Spec Gate (opt-in, default OFF) */}
            <button
              type="button"
              role="menuitem"
              data-testid="spec-mode-toggle"
              onClick={() => setPref('spec_mode', !ui.spec_mode)}
              aria-pressed={ui.spec_mode ? 'true' : 'false'}
              aria-label="Alternar modo Spec (gera especificação antes do turno)"
              title="Modo Spec — gera uma spec revisável antes de rodar o agente (opt-in)"
              className={`${chipClass} w-full justify-start ${ui.spec_mode ? 'text-apolo-textPrimary' : ''}`}
            >
              <FileText size={11} aria-hidden="true" />
              <span>Spec{ui.spec_mode ? ' · on' : ''}</span>
            </button>
            {/* MOVIDO — Skills (templates) */}
            <button
              type="button"
              role="menuitem"
              onClick={() => setShowSkillsModal(true)}
              title="Templates de skills (combos com 1 clique)"
              aria-label="Abrir templates de skills"
              className={`${chipClass} w-full justify-start`}
            >
              <Sparkles size={12} className="text-violet-400" strokeWidth={2.25} aria-hidden="true" />
              Skills
            </button>
            {/* MOVIDO — Equipe / Compare */}
            {compareMode ? (
              <CompareModeToggle
                active={!!compareMode.active}
                onToggle={(next) => {
                  if (next && ui.team_mode) setPref('team_mode', false);
                  compareMode.onToggle?.(next);
                }}
                count={compareMode.modelsCount}
              />
            ) : null}
            <TeamModeToggle
              active={!!ui.team_mode}
              onToggle={(next) => {
                if (next && compareMode?.active) compareMode.onToggle?.(false);
                setPref('team_mode', next);
              }}
            />
            {ui.team_mode ? (
              <TeamSettingsPopover
                cap={ui.team_cap}
                workerModel={ui.team_worker_model}
                readOnly={!!ui.team_read_only}
                groups={modelGroups}
                onCapChange={(n) => setPref('team_cap', n)}
                onWorkerModelChange={(m) => setPref('team_worker_model', m)}
                onReadOnlyChange={(v) => setPref('team_read_only', v)}
              />
            ) : null}
            {/* Esforço de raciocínio (Low/Med/High/XHigh/Max) — velocidade das
                respostas. Só aparece para modelos que raciocinam (reasoning);
                nos demais o parâmetro seria rejeitado, então nem mostramos. */}
            {modelSupportsReasoning(ui.model) ? (
            <div className="flex w-full items-center gap-1 px-1.5 py-1" role="group" aria-label="Esforço de raciocínio">
              <span className="mr-1 text-[10px] uppercase tracking-wide text-apolo-textTertiary">Esforço</span>
              {[
                { id: 'low', label: 'Low', title: 'Esforço baixo — respostas rápidas' },
                { id: 'med', label: 'Med', title: 'Esforço médio (padrão)' },
                { id: 'high', label: 'High', title: 'Esforço alto — raciocínio profundo' },
                { id: 'xhigh', label: 'XHigh', title: 'Esforço muito alto' },
                { id: 'max', label: 'Max', title: 'Esforço máximo — mais lento, mais caro' },
              ].map((tier) => {
                const on = (ui.effort || 'med') === tier.id;
                return (
                  <button
                    key={tier.id}
                    type="button"
                    onClick={() => setPref('effort', tier.id)}
                    aria-pressed={on}
                    title={tier.title}
                    className={
                      'rounded px-1.5 py-0.5 text-[10.5px] font-mono transition ' +
                      (on
                        ? 'bg-apolo-surface3 text-apolo-textPrimary'
                        : 'text-apolo-textTertiary hover:text-apolo-textPrimary')
                    }
                  >
                    {tier.label}
                  </button>
                );
              })}
            </div>
            ) : null}
            <button
              type="button"
              role="menuitem"
              onClick={() =>
                window.dispatchEvent(new CustomEvent('apolo:openSkillPalette'))
              }
              title="Atalhos (⌘K)"
              aria-label="Abrir Command Palette de atalhos (⌘K)"
              className={`${chipClass} w-full justify-start`}
            >
              <Command size={12} className="text-apolo-accent" aria-hidden="true" />
              Atalhos
              <kbd className="ml-auto hidden rounded bg-apolo-surface3 px-1 py-0.5 text-[9px] text-apolo-textTertiary sm:inline">⌘K</kbd>
            </button>
          </ComposerOverflowMenu>
          {/* (Pedro 2026-07-16) — mic 1º cidadão da strip: divisor cinza + ícone
              redondo (par do rx-ctxu-btn). ⌘D toggla a gravação (Whisper no
              Electron, Web Speech no browser). Instância ÚNICA com shortcut. */}
          <span aria-hidden="true" className="mx-0.5 h-4 w-px shrink-0 bg-apolo-border" />
          <MicButton
            iconOnly
            shortcut
            onTranscript={(delta) => setText((prev) => (prev ? prev + delta : delta))}
          />
          {/* Fila de envio: enquanto RODA, dá pra enfileirar (botão de envio azul)
              E parar (botão vermelho) — os dois convivem. O chip mostra quantos
              envios esperam o turno atual terminar. */}
          {queuedCount > 0 && (
            <span
              title="Mensagens na fila — rodam em sequência quando o turno atual terminar"
              className="inline-flex h-6 shrink-0 items-center gap-1 rounded-md bg-apolo-accent/10 px-2 text-[11px] font-medium text-apolo-accent"
            >
              <ClipboardList size={11} aria-hidden="true" />
              {queuedCount} na fila
            </span>
          )}
          <AnimatePresence initial={false}>
            {canQueue && (
              <motion.button
                key="queue"
                type="submit"
                title="Enviar na fila — roda quando o turno atual terminar"
                aria-label="Enviar na fila"
                initial={reduce ? false : { opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.15 }}
                className="rx-send"
              >
                <ArrowUp size={16} aria-hidden="true" />
              </motion.button>
            )}
            {running && (
              <motion.button
                key="stop"
                type="button"
                onClick={onStop}
                title="Parar o agente (Esc)"
                aria-label="Parar o agente (Esc)"
                initial={reduce ? false : { opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.15 }}
                className="rx-send rx-send--stop"
              >
                <Square size={13} fill="currentColor" aria-hidden="true" />
              </motion.button>
            )}
            {!running && (
              <motion.button
                key="send"
                type="submit"
                disabled={!canSend}
                aria-disabled={!canSend}
                title="Enviar mensagem"
                aria-label="Enviar"
                initial={reduce ? false : { opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.15 }}
                className="rx-send"
              >
                {busy ? (
                  <Loader2 size={14} className="animate-spin" aria-hidden="true" />
                ) : (
                  <ArrowUp size={16} aria-hidden="true" />
                )}
              </motion.button>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Input no topo (Cursor-style): transparente, blende no box rx-composer;
          alinhado à esquerda. */}
      <div className="relative">
        {planMode && <div className="workbench-plan-hint"><ClipboardList size={15} /><span><strong>Modo Planejamento</strong> Revise os passos antes de aprovar a execução.</span></div>}
        <textarea
          ref={textareaRef}
          rows={2}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onPaste={handlePaste}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              // Enter envia; enquanto RODA, handleSubmit roteia para a FILA. WSX: quando
              // running, NÃO exige `!busy` (a fila é síncrona; `busy` fica true o run
              // inteiro e engolia o Enter). Fora de run, `!busy` barra o duplo-envio.
              if ((running || !busy) && hasContentNow) handleSubmit(e);
            }
          }}
          placeholder={planMode ? 'Descreva o objetivo ou peça um ajuste no plano…' : running ? 'Escreva a próxima mensagem para adicionar à fila…' : 'Pergunte, peça um ajuste ou compartilhe uma ideia…'}
          aria-label="Mensagem para o agente. Enter envia, Shift+Enter quebra linha. Use / para skills e @ para arquivos."
          className="rx-composer-textarea w-full resize-y bg-transparent px-1.5 py-1.5 text-sm text-apolo-textPrimary placeholder:text-apolo-textTertiary focus:outline-none"
        />
        {/* Floating dropdowns: '/' = slash commands · '@file:'/'@folder:' = typeahead
            de arquivos do worktree. Só um aparece por vez (gatilhos distintos).
            (Pedro 2026-07-07) — via ComposerMenuPortal pra escapar o stacking
            context do `.rx-composer` (backdrop-filter): os menus ficavam ATRÁS
            do composer. Ancorados na textarea, sobem (bottom-full) e pintam
            acima de todo o chrome (z-60). */}
        <ComposerMenuPortal anchorRef={textareaRef}>
          <div className="absolute left-0 bottom-full mb-1">
            <SlashCommandAutocomplete
              input={text}
              onPick={handlePickCommand}
              anchorRef={textareaRef}
              onDismiss={() => textareaRef.current?.focus()}
            />
            {/* Cursor-style "@" menu — bare @ opens it; @file:/@folder: keeps the
                file typeahead below (mutually exclusive detectors). */}
            <ComposerAtMenu
              input={text}
              contextItems={contextItems}
              onSelect={handleAtMenuSelect}
              onDismiss={() => textareaRef.current?.focus()}
            />
            <FileMentionAutocomplete
              input={text}
              root={activeSession?.directory || ''}
              onPick={handlePickFileMention}
              onDismiss={() => textareaRef.current?.focus()}
            />
            {/* Aba-Apolo C2 — `#chat:` typeahead: referencia uma conversa passada
                como contexto (resolvido no envio via resolveAndEnrich). */}
            <ChatMentionAutocomplete
              input={text}
              currentSessionId={sessionId}
              onPick={handlePickChatMention}
              onDismiss={() => textareaRef.current?.focus()}
            />
          </div>
        </ComposerMenuPortal>
      </div>

      {/* Sprint v8.3: chips azuis pra skills ja invocadas no prompt. */}
      <InvokedSkillsBar
        skills={invokedSkills}
        onRemove={(slash) => setInvokedSkills((prev) => prev.filter((c) => c.slash_command !== slash))}
      />

      {/* Sprint v8.3: chips pra arquivos anexados (drag-drop ou file picker). */}
      <AttachmentChips files={attachments} onRemove={removeAttachment} />

      {uploadError && (
        <p role="alert" className="text-[11px] text-apolo-error">
          {uploadError}
        </p>
      )}

      {/* (Pedro 2026-06-25, sprint cursor-strip-4 R4-2) — footer-strip persistente:
          cloud/local · model · tokens · custo. O `branch` saiu daqui porque a
          GitConnectionBar agora vive logo ABAIXO (status bar Cursor-style) e
          já mostra o branch como chip clicável — evita duplicação. */}
      <ComposerFooterStrip
        branch={null}
        model={ui.model}
        running={!!running || !!busy}
        lastTurnCost={ui.lastTurnCost}
        contextUsage={contextUsage}
      />

      <SkillTemplatesModal
        open={showSkillsModal}
        onClose={() => {
          setShowSkillsModal(false);
          requestAnimationFrame(() => textareaRef.current?.focus());
        }}
      />

      {/* Sprint v11 E15 — hands-free voice modal (FAB flutuante). Era um item
          dentro do ComposerOverflowMenu; agora é FAB sempre visível (bottom-right)
          que abre modal imersivo com esfera animada. Transcrições viram mensagens
          normais na session — a conversa fica "escrita" na sidebar. O componente
          teleporta via Portal para document.body, então onde ele vive no JSX só
          afeta ownership — a posição visual é fixed na viewport inteira. */}

    </form>
  );
}
